/* eslint-disable import/no-unused-modules */
import type {
  IntegrationDescription,
  OutputDescription,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import type { Fields } from "@smartsuite/smartsuite-client";
import * as R from "remeda";
import type { LoopScope } from "../../../../../stores/automation/looping.store";
import type { ActionCandidate } from "../../../../../types/candidate";
import { ActionLabel } from "../../../../../utils/constants";
import { getOutputDescriptions } from "../../../../../utils/conditions-coverter.hook";
import { pickActionName } from "../../../../../utils/inputs";
import {
  getActionName,
  isLoopingSourceOptions,
  isSimpleTextOutput,
  isSupportedOutput,
  systemTypeFilter,
} from "../utils";

export interface TriggerActionOption {
  title: string;
  outputId: string;
  icon?: string;
  contextSpecific: boolean;
  hash?: string;
}

export interface ActionOptionGroup {
  name: string;
  options: TriggerActionOption[];
  action: ActionCandidate;
  hideNamePrefix?: boolean;
}

const outputsToOptions = (
  action: ActionCandidate,
  outputs: OutputDescription[],
  types = [ValueDescription_T.STRING],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
): TriggerActionOption[] =>
  outputs
    .filter((output) => {
      // support teamfield entire field
      if (
        output.smartsuiteFieldType === "teamfield" &&
        !output.outputId.includes(".")
      ) {
        return true;
      }
      if (isSimpleTextOutput(output)) {
        return true;
      }
      if (output.contextSpecific) {
        return systemTypeFilter(systemAllowedOptions, output);
      }

      let pass =
        output.value?.type === undefined
          ? true
          : types.includes(output.value.type);

      if (ssFieldTypes !== undefined) {
        pass =
          output.smartsuiteFieldType === undefined ||
          (ssFieldTypes.includes(output.smartsuiteFieldType as Fields) &&
            (output.value?.type === undefined ||
              types.includes(output.value.type)));
      }

      return pass;
    })
    .map((output) => ({
      title:
        output.label +
        (output.labelSuffix === undefined ? "" : `: ${output.labelSuffix}`),
      contextSpecific: output.contextSpecific,
      outputId: output.outputId,
      icon: output.icon,
      hash: action.hash,
    }));

export const actionsToOptions = (
  actions: ActionCandidate[],
  /** The action this editor belongs to, used as the upper boundary of the
   * offered sources. Conditional Logic branch conditions have no owning action
   * -- they pass `undefined` together with an `actions` list already scoped to
   * the block's own execution path, so nothing is sliced off. */
  currentAction: ActionCandidate | undefined,
  integrations: IntegrationDescription[],
  types = [ValueDescription_T.STRING],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
  looping?: LoopScope,
): ActionOptionGroup[] => {
  const loopingSourceFindActionId =
    looping?.source?.oneofKind === "recordList" &&
    looping.source.recordList.recordList.oneofKind === "actionReference"
      ? looping.source.recordList.recordList.actionReference.instanceId
      : undefined;

  return R.pipe(
    actions,
    R.takeWhile(
      (action) =>
        currentAction === undefined || action.hash !== currentAction.hash,
    ),
    R.filter(
      (action) => !isLoopingSourceOptions(action, currentAction, looping),
    ),
    R.filter((action) => isSupportedOutput(action, actions, currentAction)),
    R.filter(
      (action) =>
        action.actionReference?.instanceId !== loopingSourceFindActionId,
    ),
    R.map((action) => ({
      action,
      hideNamePrefix:
        action.actionReference?.actionId === ActionLabel.findRecords ||
        (action.actionReference?.actionId === ActionLabel.ai &&
          pickActionName(action.inputs, ActionLabel.ai) !== undefined),
      name: getActionName(action, integrations),
      options: outputsToOptions(
        action,
        getOutputDescriptions(action),
        types,
        ssFieldTypes,
        systemAllowedOptions,
      ),
    })),
  );
};
